import { Inject, Injectable } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { StateService } from '@uirouter/core';
import { cloneDeep } from 'lodash';

import { storePurchaseAPIInjector } from 'src/app/api/infra/store-api-service.injector';
import { BillingApiService } from 'src/app/api/services/billing-api.service';
import { StorePurchaseApi } from 'src/app/api/store-api';
import { CompanyStateService } from 'src/app/auth/services/company-state.service';
import { UserStateService } from 'src/app/auth/services/user-state.service';
import {
  AI_CREDITS_MIN_QUANTITY,
  AI_CREDITS_QUANTITY_STEP,
  PlansService,
} from 'src/app/components/plans/plans.service';
import { AiCreditsService } from 'src/app/settings/services/ai-credits.service';
import { AddressUtilsService } from 'src/app/shared/services/address-utils.service';
import { ContactUtilsService } from 'src/app/shared/services/contact-utils.service';

import {
  PaymentScheduleOption,
  PaymentScheduleType,
} from '../components/payment-schedule-picker/payment-schedule-picker.types';
import {
  BasePurchaseDetails,
  BasePurchaseService,
} from './base-purchase.service';
import { CreditCardService } from './credit-card.service';

export type AiCreditsEstimate = {
  amountDueNow: number;
  currencyCode: string;
  total?: number;
  subTotal?: number;
  taxes?: any[];
  totalTax?: number;
  coupons?: any[];
  couponAmount?: number;
  nextRenewalDate?: number;
  nextRenewalTotal?: number;
  estimateError?: string;
};

type AiCreditsEstimateSummary = {
  total: number;
  subTotal: number;
  taxes: any[];
  totalTax: number;
  coupons: any[];
  couponAmount: number;
  currencyCode: string;
};

@Injectable({
  providedIn: 'root',
})
export class PurchaseAiCreditsService extends BasePurchaseService {
  public loading = false;
  public apiError: string | null = null;
  public purchase: BasePurchaseDetails = {};
  public scheduleOptions: PaymentScheduleOption[] = [];
  public estimate: AiCreditsEstimate = {
    amountDueNow: 0,
    currencyCode: 'usd',
  };
  public couponCode = '';
  public existingAiCreditsSubscriptionId: string | null = null;
  public currentMonthlyCredits = 0;

  private reloadCallback: () => void;
  private purchaseIntent: { intentId?: string } | null = null;
  private planSubscriptionCreditsTotal = 0;

  readonly form = new FormGroup({
    quantity: new FormControl<number>(AI_CREDITS_MIN_QUANTITY, {
      nonNullable: true,
      validators: [
        Validators.required,
        Validators.min(AI_CREDITS_MIN_QUANTITY),
        this.multipleOfStepValidator(AI_CREDITS_QUANTITY_STEP),
      ],
    }),
    scheduleProductCode: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
  });

  constructor(
    private stateService: StateService,
    private plansService: PlansService,
    private companyStateService: CompanyStateService,
    private userStateService: UserStateService,
    private addressUtils: AddressUtilsService,
    private contactUtils: ContactUtilsService,
    private creditCardService: CreditCardService,
    private billingApiService: BillingApiService,
    private aiCreditsService: AiCreditsService,
    @Inject(storePurchaseAPIInjector) private storeApiService: StorePurchaseApi,
  ) {
    super();
  }

  init(): Promise<void> {
    this._clearMessages();
    this.purchase = {
      couponCode: '',
      billingAddress: this.addressUtils.copyAddress(
        this.companyStateService.getCopyOfSelectedCompany(),
      ),
      contact: this.contactUtils.copyContactObj(
        this.userStateService.getCopyOfProfile(),
      ),
      estimate: {
        taxes: [],
        subTotal: 0,
        total: 0,
        coupons: [],
        couponAmount: 0,
        totalTax: 0,
        currency: this._getCurrency(),
      },
    };
    this.form.patchValue({
      quantity: AI_CREDITS_MIN_QUANTITY,
    });

    return this._loadExistingAiCreditsSubscription()
      .then(() => this._loadPlanSubscriptionCreditsTotal())
      .then(() => {
        this.scheduleOptions = this._buildScheduleOptions();
        const defaultProductCode =
          this.scheduleOptions.find((o) => o.scheduleType === 'annual')
            ?.productCode ??
          this.scheduleOptions[0]?.productCode ??
          this.plansService.getAiCreditsPlans().find((plan) => !!plan.yearly)
            ?.productCode ??
          '';
        this.form.patchValue({ scheduleProductCode: defaultProductCode });
        this._refreshScheduleOptionTotals();
        return this.refreshEstimate();
      })
      .then(() =>
        this.creditCardService.initPaymentMethods(true).finally(() => {
          this.creditCardService.paymentMethods.paymentMethod = 'card';
          this.creditCardService.paymentMethods.newCreditCard.billingAddress =
            this.purchase.billingAddress;
        }),
      );
  }

  get selectedPlan() {
    const productCode = this.form.controls.scheduleProductCode.value;
    return productCode ? this.plansService.getPlan(productCode) : undefined;
  }

  get selectedScheduleType(): PaymentScheduleType | null {
    const option = this.scheduleOptions.find(
      (o) => o.productCode === this.form.controls.scheduleProductCode.value,
    );
    return option?.scheduleType ?? null;
  }

  get isAnnualSubscriptionSelected(): boolean {
    return this.selectedScheduleType === 'annual';
  }

  get isOneTimePackSelected(): boolean {
    return this.plansService.isOneTimePlan(this.selectedPlan);
  }

  get forceCreditCardPayment(): boolean {
    return this.isOneTimePackSelected;
  }

  get totalDue(): number {
    return this.estimate.amountDueNow;
  }

  get payButtonLabel(): string {
    if (this.totalDue > 0) {
      return `Pay $${this.totalDue.toFixed(2)} Now`;
    }
    return 'Continue';
  }

  isPayButtonDisabled(): boolean {
    return (
      this.form.invalid ||
      this.loading ||
      (!!this.existingAiCreditsSubscriptionId && !this.isOneTimePackSelected)
    );
  }

  onQuantityOrScheduleChanged(): void {
    this._refreshScheduleOptionTotals();
    this.refreshEstimate();
  }

  refreshEstimate(): Promise<void> {
    if (this.form.invalid) {
      return Promise.resolve();
    }

    if (
      this.isAnnualSubscriptionSelected &&
      this.existingAiCreditsSubscriptionId
    ) {
      return this._estimateSubscriptionUpdate();
    }

    return this._estimateOneTimePurchase();
  }

  getEstimate(): Promise<void> {
    return this.refreshEstimate();
  }

  estimateAnnualSubscription(quantity: number): Promise<AiCreditsEstimate> {
    const annualPlan = this.plansService
      .getAiCreditsPlans()
      .find((plan) => !!plan.yearly);

    if (!annualPlan) {
      return Promise.reject(new Error('AI Credits annual plan not found.'));
    }

    this.form.patchValue({
      quantity,
      scheduleProductCode: annualPlan.productCode,
    });

    return this.refreshEstimate().then(() => {
      if (this.estimate.estimateError) {
        throw new Error(this.estimate.estimateError);
      }

      return this.estimate;
    });
  }

  continue(): Promise<boolean> {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return Promise.resolve(false);
    }

    this._syncAddOnPlan();
    this.purchase.couponCode = this.couponCode;
    return this.refreshEstimate().then(() => !this.estimate.estimateError);
  }

  cancel(): void {
    this.stateService.go('apps.billing.subscription', {
      subscriptionId: this.existingAiCreditsSubscriptionId || undefined,
    });
  }

  applyCouponCode(): Promise<void> {
    this.purchase.couponCode = this.couponCode;
    return this.refreshEstimate();
  }

  clearCouponCode(): void {
    this.couponCode = '';
    this.purchase.couponCode = '';
    void this.refreshEstimate();
  }

  validatePaymentMethod(): Promise<void> {
    this.purchase.checkoutError = null;

    if (this.creditCardService.paymentMethods.paymentMethod === 'invoice') {
      return Promise.resolve();
    }

    this.loading = true;
    return this.creditCardService.validatePaymentMethod().finally(() => {
      this.loading = false;
    });
  }

  preparePaymentIntent(): Promise<void> {
    const paymentMethods = this.creditCardService.paymentMethods;

    if (paymentMethods.paymentMethod === 'invoice') {
      return Promise.resolve();
    }

    if (
      this.isAnnualSubscriptionSelected &&
      this.existingAiCreditsSubscriptionId
    ) {
      return this._prepareSubscriptionUpdatePayment();
    }

    this.loading = true;
    const jsonData = this._getOrderAsJson(this.form.controls.quantity.value);

    return this.storeApiService
      .preparePurchase(jsonData)
      .then((response) => {
        if (response.error) {
          this.purchase.checkoutError = response.error;
          return Promise.reject(response.error);
        }
        paymentMethods.intentResponse = response;
        if (response.authenticationRequired) {
          return this.creditCardService.handleCardAction(response.intentSecret);
        }
      })
      .catch((error) => {
        this.purchase.checkoutError =
          error?.message || 'Something went wrong, please retry';
        return Promise.reject(error);
      })
      .finally(() => {
        this.loading = false;
      });
  }

  completePayment(): Promise<void> {
    this.purchase.checkoutError = null;
    this.loading = true;

    if (
      this.isAnnualSubscriptionSelected &&
      this.existingAiCreditsSubscriptionId
    ) {
      return this._completeSubscriptionUpdate();
    }

    const quantity = this.form.controls.quantity.value;
    const jsonData = this._getOrderAsJson(quantity);

    return this.storeApiService
      .purchase(jsonData)
      .then(() => {
        this.purchase.reloadingCompany = false;
        this.reloadCallback?.();
      })
      .catch((result) => {
        this.purchase.checkoutError =
          result?.message || 'There was an unknown error with the payment.';
      })
      .finally(() => {
        this.loading = false;
      });
  }

  onCompanyReload(reloadCallback: () => void): void {
    this.reloadCallback = reloadCallback;
  }

  private _syncAddOnPlan(): void {
    const quantity = this.form.controls.quantity.value;
    const scheduleLabel =
      this.scheduleOptions.find(
        (o) => o.productCode === this.form.controls.scheduleProductCode.value,
      )?.title ?? 'AI Credits';

    this.purchase.addOnPlan = {
      name: `${quantity} ${scheduleLabel}`,
      productCode: this.form.controls.scheduleProductCode.value,
      licenses: quantity,
      isMonthly: false,
    };
  }

  private _syncPurchaseEstimateFromAiCredits(): void {
    const totalCents =
      this.estimate.total ?? Math.round(this.estimate.amountDueNow * 100);
    this.purchase.estimate = {
      ...this.purchase.estimate,
      subTotal: this.estimate.subTotal ?? totalCents,
      total: totalCents,
      totalTax: this.estimate.totalTax ?? 0,
      taxes: this.estimate.taxes ?? this.purchase.estimate?.taxes ?? [],
      coupons: this.estimate.coupons ?? this.purchase.estimate?.coupons ?? [],
      couponAmount:
        this.estimate.couponAmount ?? this.purchase.estimate?.couponAmount ?? 0,
      currency: this.estimate.currencyCode,
      estimateError: this.estimate.estimateError,
    };
  }

  private _buildScheduleOptions(): PaymentScheduleOption[] {
    const currencyCode = this._getCurrency();
    const aiCreditsPlans = this.plansService.getAiCreditsPlans();
    const annualPlan = aiCreditsPlans.find((plan) => !!plan.yearly);
    const oneTimePlan = aiCreditsPlans.find((plan) => !!plan.oneTime);
    const options: PaymentScheduleOption[] = [];

    if (annualPlan) {
      options.push({
        productCode: annualPlan.productCode,
        scheduleType: 'annual',
        title: 'Annual Subscription',
        description: '',
        currencyCode,
        creditsCount: annualPlan.aiCreditsCount,
        monthlyPrice: annualPlan.yearly?.priceDisplayMonth,
        annualSavings: annualPlan.yearly?.save,
        newMonthlyTotalCredits: this._getNewMonthlyTotalCredits(),
        ...(this.existingAiCreditsSubscriptionId
          ? { existingSubscription: this.existingAiCreditsSubscriptionId }
          : {}),
      });
    }

    if (oneTimePlan) {
      options.push({
        productCode: oneTimePlan.productCode,
        scheduleType: 'one-time',
        title: 'One-time AI Credits Pack',
        description: '',
        currencyCode,
        creditsCount: oneTimePlan.aiCreditsCount,
        oneTimePrice: oneTimePlan.oneTime?.billAmount,
      });
    }

    return options;
  }

  private _refreshScheduleOptionTotals(): void {
    this.scheduleOptions = this.scheduleOptions.map((option) => {
      if (option.scheduleType !== 'annual') {
        return option;
      }
      return {
        ...option,
        newMonthlyTotalCredits: this._getNewMonthlyTotalCredits(),
      };
    });
  }

  private _getNewMonthlyTotalCredits(): number {
    return (
      this.planSubscriptionCreditsTotal + this.form.controls.quantity.value
    );
  }

  private _loadExistingAiCreditsSubscription(): Promise<void> {
    this.loading = true;
    return this.billingApiService
      .getSubscriptions({ count: 100 })
      .then((response) => {
        const items = response?.items || [];
        const match = items.find((item) => {
          const planId = item.subscription?.plan?.item_price_id;
          if (!planId) {
            return false;
          }
          const plan = this.plansService.getPlanById(planId);
          return (
            this.plansService.isAdditionalAiCreditsPlan(plan) && !!plan.yearly
          );
        });

        if (match?.subscription) {
          this.existingAiCreditsSubscriptionId = match.subscription.id;
          this.currentMonthlyCredits = match.subscription.plan?.quantity || 0;
          return;
        }

        this.existingAiCreditsSubscriptionId = null;
        this.currentMonthlyCredits = 0;
      })
      .catch(() => {
        this.existingAiCreditsSubscriptionId = null;
        this.currentMonthlyCredits = 0;
      })
      .finally(() => {
        this.loading = false;
      });
  }

  private _loadPlanSubscriptionCreditsTotal(): Promise<void> {
    return this.aiCreditsService
      .getAICredits()
      .then((credits) => {
        this.planSubscriptionCreditsTotal =
          credits.planSubscriptionCreditsTotal;
      })
      .catch(() => {
        this.planSubscriptionCreditsTotal = 0;
      });
  }

  private _estimateSubscriptionUpdate(): Promise<void> {
    const quantity =
      this.form.controls.quantity.value / AI_CREDITS_QUANTITY_STEP;
    const subscriptionId = this.existingAiCreditsSubscriptionId;
    const plan = this.plansService
      .getAiCreditsPlans()
      .find(
        (p) => p.productCode === this.form.controls.scheduleProductCode.value,
      );
    if (!subscriptionId || !plan) {
      return Promise.resolve();
    }

    this.loading = true;
    this._clearMessages();

    const planId = this._getChargebeePlanId(plan);
    const newQty = this.currentMonthlyCredits + quantity;

    return this.billingApiService
      .estimateSubscriptionUpdate(
        [{ subscriptionId, id: planId, qty: newQty }],
        this.couponCode,
        false,
      )
      .then((result) => {
        const estimateItem = result?.items?.[0];
        const estimateSummary = this._getAiCreditsEstimateSummary(
          result?.items || [],
        );
        this.estimate = {
          amountDueNow: this._getInvoiceTotalFromEstimate(estimateItem),
          currencyCode: estimateSummary.currencyCode,
          total: estimateSummary.total,
          subTotal: estimateSummary.subTotal,
          taxes: estimateSummary.taxes,
          totalTax: estimateSummary.totalTax,
          coupons: estimateSummary.coupons,
          couponAmount: estimateSummary.couponAmount,
          nextRenewalDate: estimateItem?.invoice_estimate?.date
            ? (estimateItem.invoice_estimate.date + 365 * 24 * 3600) * 1000
            : undefined,
          nextRenewalTotal: estimateItem?.invoice_estimate?.total
            ? estimateItem.invoice_estimate.total / 100
            : undefined,
        };
        this._syncAddOnPlan();
        this._syncPurchaseEstimateFromAiCredits();
      })
      .catch((error) => {
        this.estimate.estimateError =
          error?.message ||
          'An unexpected error has occurred. Please try again.';
        this._syncPurchaseEstimateFromAiCredits();
      })
      .finally(() => {
        this.loading = false;
      });
  }

  private _estimateOneTimePurchase(): Promise<void> {
    this.loading = true;
    this._clearMessages();

    return this.storeApiService
      .estimatePurchase(this._getEstimateAsJson())
      .then((results) => {
        const estimateSummary = this._getAiCreditsEstimateSummary(
          results?.items || [],
        );

        this.estimate = {
          amountDueNow: estimateSummary.total / 100,
          currencyCode: estimateSummary.currencyCode,
          total: estimateSummary.total,
          subTotal: estimateSummary.subTotal,
          taxes: estimateSummary.taxes,
          totalTax: estimateSummary.totalTax,
          coupons: estimateSummary.coupons,
          couponAmount: estimateSummary.couponAmount,
        };
        this._syncAddOnPlan();
        this._syncPurchaseEstimateFromAiCredits();
      })
      .catch((error) => {
        this.estimate.estimateError =
          error?.message ||
          'An unexpected error has occurred. Please try again.';
        this._syncPurchaseEstimateFromAiCredits();
      })
      .finally(() => {
        this.loading = false;
      });
  }

  private _prepareSubscriptionUpdatePayment(): Promise<void> {
    const subscriptionId = this.existingAiCreditsSubscriptionId;
    const plan = this.selectedPlan;
    if (!subscriptionId || !plan?.productCode) {
      return Promise.resolve();
    }

    this.loading = true;
    this._clearMessages();

    const planId = this._getChargebeePlanId(plan);
    const newQty =
      this.currentMonthlyCredits + this.form.controls.quantity.value;
    const companyId = this.companyStateService.getCopyOfSelectedCompany().id;

    return this.billingApiService
      .prepareUpdateSubscription(
        [{ subscriptionId, companyId, id: planId, qty: newQty }],
        this.couponCode,
        false,
      )
      .then((response) => {
        this.purchaseIntent = response;
      })
      .catch((error) => {
        this.purchase.checkoutError =
          error?.message || 'Something went wrong, please retry';
        return Promise.reject(error);
      })
      .finally(() => {
        this.loading = false;
      });
  }

  private _completeSubscriptionUpdate(): Promise<void> {
    const subscriptionId = this.existingAiCreditsSubscriptionId;
    const plan = this.selectedPlan;
    if (!subscriptionId || !plan?.productCode) {
      return Promise.resolve();
    }

    const planId = this._getChargebeePlanId(plan);
    const newQty =
      this.currentMonthlyCredits + this.form.controls.quantity.value;
    const companyId = this.companyStateService.getCopyOfSelectedCompany().id;
    const paymentMethods = this.creditCardService.paymentMethods;
    const card =
      paymentMethods.paymentMethod === 'invoice'
        ? null
        : {
            intentId: this.purchaseIntent?.intentId ?? null,
          };

    return this.billingApiService
      .updateSubscription(
        [{ subscriptionId, companyId, id: planId, qty: newQty }],
        this.couponCode,
        false,
        card,
      )
      .catch((error) => {
        this.purchase.checkoutError =
          error?.message || 'There was an unknown error with the payment.';
        return Promise.reject(error);
      })
      .finally(() => {
        this.loading = false;
      });
  }

  private _getEstimateAsJson(): string {
    const obj = {
      billTo: this.addressUtils.copyAddress(this.purchase.billingAddress),
      shipTo: this.addressUtils.copyAddress(this.purchase.billingAddress),
      couponCode: this.couponCode,
      items: this._getOrderItems(),
    };
    return JSON.stringify(obj);
  }

  private _getOrderAsJson(quantity: number): string {
    const paymentMethods = this.creditCardService.paymentMethods;
    const items = this._getOrderItems(quantity);
    const card = paymentMethods.selectedCard.payment_source
      ? paymentMethods.selectedCard.payment_source
      : paymentMethods.selectedCard;
    const intentId = paymentMethods.intentResponse
      ? paymentMethods.intentResponse.intentId
      : null;
    const cardData: any =
      paymentMethods.paymentMethod === 'invoice'
        ? null
        : {
            cardId: card?.id,
            intentId,
            setupIntentId: null,
            isDefault: card?.isDefault ? true : false,
          };

    const obj = {
      billTo: this.addressUtils.copyAddress(this.purchase.billingAddress),
      shipTo: this.addressUtils.copyAddress(this.purchase.billingAddress),
      couponCode: this.couponCode,
      items,
      purchaseOrderNumber: paymentMethods.purchaseOrderNumber,
      card: cardData,
      paymentMethodId: this.creditCardService.getPaymentMethodId(),
      invoiceImmediately: true,
      useUnbilledCharges: false,
    };
    return JSON.stringify(obj);
  }

  private _getOrderItems(quantity?: number) {
    const plan = this.selectedPlan;
    if (!plan) {
      return [];
    }
    const qty =
      (quantity ?? this.form.controls.quantity.value) /
      AI_CREDITS_QUANTITY_STEP;
    return [
      {
        id: this._getChargebeePlanId(plan),
        qty,
      },
    ];
  }

  private _getChargebeePlanId(plan: {
    productCode: string;
    oneTime?: unknown;
  }): string {
    const currency = this._getCurrency();
    const suffix = plan.oneTime ? '' : '01y';
    return `${plan.productCode}-${currency}${suffix}`;
  }

  private _getCurrency(): string {
    const country =
      this.purchase.billingAddress?.country ??
      this.companyStateService.getCopyOfSelectedCompany().country;
    return country === 'CA' ? 'cad' : 'usd';
  }

  private _getInvoiceTotalFromEstimate(estimate: any): number {
    if (!estimate) {
      return 0;
    }
    if (!estimate.subscription_estimate?.id && estimate.next_invoice_estimate) {
      return estimate.next_invoice_estimate.amount_due / 100;
    }
    return (estimate?.invoice_estimate?.amount_due || 0) / 100;
  }

  private _getAiCreditsEstimateSummary(
    estimateItems: any[],
  ): AiCreditsEstimateSummary {
    const summary: AiCreditsEstimateSummary = {
      total: 0,
      subTotal: 0,
      taxes: [],
      totalTax: 0,
      coupons: [],
      couponAmount: 0,
      currencyCode: this._getCurrency(),
    };
    const taxes: Record<string, any> = {};
    const coupons: Record<string, any> = {};

    estimateItems.forEach((result) => {
      const invoiceEstimate =
        result.next_invoice_estimate || result.invoice_estimate;
      if (!invoiceEstimate) {
        return;
      }

      for (const tax of invoiceEstimate.taxes || []) {
        if (!taxes[tax.name]) {
          taxes[tax.name] = cloneDeep(tax);
          taxes[tax.name].amount = 0;
        }

        taxes[tax.name].amount += tax.amount || 0;
        summary.totalTax += tax.amount || 0;
      }

      for (const coupon of invoiceEstimate.discounts || []) {
        if (!coupon.entity_id) {
          continue;
        }
        if (!coupons[coupon.entity_id]) {
          coupons[coupon.entity_id] = cloneDeep(coupon);
          coupons[coupon.entity_id].amount = 0;
        }

        coupons[coupon.entity_id].amount += coupon.amount || 0;
        summary.couponAmount += coupon.amount || 0;
      }

      summary.total += invoiceEstimate.total || 0;
      summary.subTotal +=
        invoiceEstimate.sub_total ?? invoiceEstimate.total ?? 0;
      summary.currencyCode =
        invoiceEstimate.currency_code?.toLowerCase() || summary.currencyCode;
    });

    summary.taxes = Object.values(taxes);
    summary.coupons = Object.values(coupons);

    return summary;
  }

  private _clearMessages(): void {
    this.apiError = null;
    this.estimate.estimateError = undefined;
  }

  private multipleOfStepValidator(step: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (value == null || value === '') {
        return null;
      }
      if (typeof value === 'number' && value % step !== 0) {
        return { multipleOf: { step, actual: value } };
      }
      return null;
    };
  }
}
